import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, API } from "@/lib/api";
import {
  HERO_QUOTE,
  HISTORIA,
  LUGAR,
  LLEGADA,
  RECOMENDACIONES,
  CONSEJOS,
  NATURALEZA,
  PROVISIONES,
  AMENIDADES,
  ESTANCIA,
  REGLAMENTO,
} from "@/lib/experienciaContent";
import {
  Upload as UploadIcon,
  RotateCcw,
  Save,
  Image as ImageIcon,
  Video as VideoIcon,
  Trash2,
  Check,
} from "lucide-react";

/**
 * Portal Content Manager (Admin)
 *
 * Sidebar of portal sections + editor form.
 * MVP: editable eyebrow / title / intro / criticalNote / hero (image or video + caption).
 * Form pre-fills with static defaults so the user only edits what needs to change.
 */

const SECTIONS = [
  { key: "hero",          label: "Hero del portal",   critical: false },
  { key: "provisiones",   label: "Antes de venir",    critical: true  },
  { key: "estancia",      label: "Tu estancia",       critical: false },
  { key: "llegar",        label: "Cómo llegar",       critical: false },
  { key: "amenidades",    label: "Amenidades",        critical: false },
  { key: "lugar",         label: "Descubre el lugar", critical: false },
  { key: "historia",      label: "La historia",       critical: false },
  { key: "consejos",      label: "Cómo disfrutar",    critical: false },
  { key: "naturaleza",    label: "Naturaleza",        critical: true  },
  { key: "asador",        label: "Reserva del asador", critical: false },
  { key: "reglamento",    label: "Convivencia",       critical: false },
  { key: "recomendaciones", label: "Recomendaciones", critical: false },
  { key: "antes",         label: "Antes de irte",     critical: false },
  { key: "faq_public",    label: "FAQ pública",       critical: false },
  { key: "faq_private",   label: "FAQ privada",       critical: false },
];

/**
 * Static defaults per section — mirrors the copy currently used by the portal.
 * Loaded into the form when no CMS override exists, so the editor is never blank.
 */
const SECTION_DEFAULTS = {
  hero:          { eyebrow: "TU PORTAL PRIVADO", title: "Portal del huésped", intro: HERO_QUOTE, criticalNote: "" },
  historia:      { eyebrow: HISTORIA.eyebrow,    title: HISTORIA.title,    intro: HISTORIA.body?.[0] || "",       criticalNote: "" },
  lugar:         { eyebrow: LUGAR.eyebrow,       title: LUGAR.title,       intro: "",                             criticalNote: "" },
  llegar:        { eyebrow: LLEGADA.eyebrow,     title: LLEGADA.title,     intro: LLEGADA.intro || "",            criticalNote: "" },
  provisiones:   { eyebrow: PROVISIONES.eyebrow, title: PROVISIONES.title, intro: "",                             criticalNote: PROVISIONES.criticalNote || "" },
  estancia:      { eyebrow: ESTANCIA.eyebrow,    title: ESTANCIA.title,    intro: "",                             criticalNote: "" },
  amenidades:    { eyebrow: AMENIDADES.eyebrow,  title: AMENIDADES.title,  intro: AMENIDADES.intro || "",         criticalNote: "" },
  naturaleza:    { eyebrow: NATURALEZA.eyebrow,  title: NATURALEZA.title,  intro: NATURALEZA.intro || "",         criticalNote: NATURALEZA.criticalNote || "" },
  reglamento:    { eyebrow: REGLAMENTO.eyebrow,  title: REGLAMENTO.title,  intro: REGLAMENTO.intro || "",         criticalNote: "" },
  consejos:      { eyebrow: CONSEJOS.eyebrow,    title: CONSEJOS.title,    intro: CONSEJOS.intro || "",           criticalNote: "" },
  recomendaciones: { eyebrow: RECOMENDACIONES.eyebrow, title: RECOMENDACIONES.title, intro: "",                    criticalNote: "" },
  asador:        { eyebrow: "RESERVA DEL ASADOR", title: "Coordina tu horario del asador.", intro: "Solicita tu horario por WhatsApp al llegar. Funciona con reserva y hay días de mucha demanda.", criticalNote: "" },
  antes:         { eyebrow: "ANTES DE IRTE",     title: "Cómo cerrar tu estancia.", intro: "",                     criticalNote: "" },
  faq_public:    { eyebrow: "PREGUNTAS FRECUENTES", title: "Todo lo que quieres saber antes de reservar.", intro: "", criticalNote: "" },
  faq_private:   { eyebrow: "PREGUNTAS FRECUENTES", title: "Todo lo que quieres saber.", intro: "", criticalNote: "" },
};

const IMG_TYPES = ["image/jpeg", "image/png", "image/webp"];
const VID_TYPES = ["video/mp4", "video/webm", "video/quicktime"];

export default function PortalContentManager({ token }) {
  const [selected, setSelected] = useState("llegar");
  const [overrides, setOverrides] = useState({}); // key -> full doc
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // form state (local, applies to selected section)
  const [form, setForm] = useState({
    eyebrow: "",
    title: "",
    intro: "",
    criticalNote: "",
    heroKind: null,        // "image" | "video" | null
    heroStoragePath: null,
    heroCaption: "",
  });

  const authHeaders = { Authorization: `Bearer ${token}` };

  // Load list once
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/portal/sections", { headers: authHeaders });
        const map = {};
        for (const s of data.sections || []) map[s.key] = s;
        setOverrides(map);
      } catch (e) {
        toast.error("No se pudo cargar el contenido del portal");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // When selected changes, hydrate the form (override wins; else use static defaults)
  useEffect(() => {
    const doc = overrides[selected];
    const c = doc?.content || {};
    const hero = doc?.hero || null;
    const defaults = SECTION_DEFAULTS[selected] || {};
    setForm({
      eyebrow: c.eyebrow || defaults.eyebrow || "",
      title: c.title || defaults.title || "",
      intro: c.intro || defaults.intro || "",
      criticalNote: c.criticalNote || defaults.criticalNote || "",
      heroKind: hero?.kind || null,
      heroStoragePath: hero?.storage_path || null,
      heroCaption: hero?.caption || "",
    });
  }, [selected, overrides]);

  const selectedMeta = useMemo(() => SECTIONS.find((s) => s.key === selected), [selected]);

  const heroPreviewUrl = form.heroStoragePath ? `${API}/portal/assets/${form.heroStoragePath}` : null;

  const handleUpload = async (file) => {
    if (!file) return;
    const isImg = IMG_TYPES.includes(file.type);
    const isVid = VID_TYPES.includes(file.type);
    if (!isImg && !isVid) {
      toast.error("Formato no permitido. Usa JPG/PNG/WebP o MP4/WebM/MOV.");
      return;
    }
    const maxMB = isVid ? 120 : 12;
    if (file.size / 1024 / 1024 > maxMB) {
      toast.error(`Archivo demasiado grande (máx ${maxMB} MB).`);
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("section_key", selected);
    fd.append("kind", "hero");
    setUploading(true);
    try {
      const { data } = await api.post("/admin/portal/upload", fd, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({
        ...f,
        heroKind: data.kind === "video" ? "video" : "image",
        heroStoragePath: data.storage_path,
      }));
      toast.success(`${data.kind === "video" ? "Video" : "Imagen"} subida correctamente.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir el archivo");
    } finally {
      setUploading(false);
    }
  };

  const clearHero = () => {
    setForm((f) => ({ ...f, heroKind: null, heroStoragePath: null, heroCaption: "" }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const content = {};
      if (form.eyebrow.trim())      content.eyebrow = form.eyebrow.trim();
      if (form.title.trim())        content.title = form.title.trim();
      if (form.intro.trim())        content.intro = form.intro.trim();
      if (form.criticalNote.trim()) content.criticalNote = form.criticalNote.trim();

      const hero = form.heroStoragePath
        ? { kind: form.heroKind || "image", storage_path: form.heroStoragePath, caption: form.heroCaption.trim() || null }
        : null;

      const { data } = await api.put(
        `/admin/portal/sections/${selected}`,
        { content, hero },
        { headers: authHeaders }
      );
      setOverrides((o) => ({ ...o, [selected]: data.section }));
      toast.success("Cambios guardados. Los huéspedes verán el contenido actualizado.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("¿Restaurar esta sección a los valores por defecto? Se perderá tu edición.")) return;
    try {
      await api.delete(`/admin/portal/sections/${selected}`, { headers: authHeaders });
      setOverrides((o) => {
        const cp = { ...o };
        delete cp[selected];
        return cp;
      });
      toast.success("Sección restaurada a los valores por defecto.");
    } catch (e) {
      toast.error("No se pudo restaurar");
    }
  };

  return (
    <div data-testid="portal-content-manager" className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
      {/* Sidebar */}
      <aside className="lg:sticky lg:top-24 lg:self-start">
        <div className="mb-4">
          <p className="text-[10px] tracking-[0.32em] uppercase text-stone-500 mb-2">Secciones</p>
          <p className="text-xs text-stone-500 leading-relaxed">
            Edita textos y sube fotos o videos por sección. Los cambios se ven de inmediato en el portal privado.
          </p>
        </div>
        <ul className="space-y-1">
          {SECTIONS.map((s) => {
            const isOverridden = !!overrides[s.key];
            const isSelected = s.key === selected;
            return (
              <li key={s.key}>
                <button
                  type="button"
                  onClick={() => setSelected(s.key)}
                  data-testid={`portal-cms-section-${s.key}`}
                  className={`w-full flex items-center justify-between text-left px-3 py-2 text-sm transition-colors ${
                    isSelected
                      ? "bg-stone-900 text-white"
                      : "text-stone-700 hover:bg-stone-100"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {s.critical && <span className="w-1.5 h-1.5 rounded-full bg-[#B84A2C]" />}
                    <span>{s.label}</span>
                  </span>
                  {isOverridden && (
                    <Check className={`w-3.5 h-3.5 ${isSelected ? "text-[#F2CC8F]" : "text-[#5F8B76]"}`} />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </aside>

      {/* Editor */}
      <section>
        <div className="mb-8 flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-stone-500 mb-2">
              Editando · {selectedMeta?.critical ? "sección crítica" : "sección"}
            </p>
            <h2 className="font-serif-display text-3xl text-stone-900">{selectedMeta?.label}</h2>
          </div>
          <div className="flex gap-2">
            {overrides[selected] && (
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                data-testid="portal-cms-reset-btn"
                className="rounded-none border-stone-300 text-stone-700"
              >
                <RotateCcw className="w-4 h-4 mr-2" /> Restaurar
              </Button>
            )}
            <Button
              type="button"
              onClick={handleSave}
              disabled={saving || loading}
              data-testid="portal-cms-save-btn"
              className="rounded-none bg-[#E07A5F] hover:bg-[#D3674B] text-white"
            >
              {saving ? "Guardando…" : <><Save className="w-4 h-4 mr-2" /> Guardar cambios</>}
            </Button>
          </div>
        </div>

        {/* Hero uploader */}
        <div className="mb-10 p-6 bg-stone-50 border border-stone-200">
          <p className="text-[10px] tracking-[0.32em] uppercase text-stone-500 mb-4">Imagen o video de la sección</p>
          {heroPreviewUrl ? (
            <div className="mb-4">
              {form.heroKind === "video" ? (
                <video
                  src={heroPreviewUrl}
                  controls
                  data-testid="portal-cms-hero-preview-video"
                  className="w-full max-h-[420px] bg-black"
                />
              ) : (
                <img
                  src={heroPreviewUrl}
                  alt="preview"
                  data-testid="portal-cms-hero-preview-image"
                  className="w-full max-h-[420px] object-cover"
                />
              )}
              <div className="mt-3">
                <Input
                  value={form.heroCaption}
                  onChange={(e) => setForm({ ...form, heroCaption: e.target.value })}
                  placeholder="Caption editorial (ej. 'El arco de entrada — cuando lo veas, ya llegaste')"
                  data-testid="portal-cms-hero-caption"
                  className="rounded-none border-stone-300"
                />
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={clearHero}
                  data-testid="portal-cms-hero-clear-btn"
                  className="rounded-none border-stone-300 text-stone-700"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Quitar
                </Button>
                <label className="inline-flex items-center border border-stone-300 hover:bg-stone-100 px-3 py-2 text-sm cursor-pointer transition-colors">
                  <UploadIcon className="w-4 h-4 mr-2" />
                  Reemplazar
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime"
                    onChange={(e) => handleUpload(e.target.files?.[0])}
                    disabled={uploading}
                    data-testid="portal-cms-hero-replace-input"
                    className="sr-only"
                  />
                </label>
              </div>
            </div>
          ) : (
            <label
              data-testid="portal-cms-hero-dropzone"
              className={`flex flex-col items-center justify-center gap-3 p-10 border-2 border-dashed transition-colors cursor-pointer ${
                uploading ? "border-[#E07A5F] bg-[#FBEEEA]" : "border-stone-300 hover:border-stone-900 hover:bg-white"
              }`}
            >
              {uploading ? (
                <p className="text-sm text-stone-700">Subiendo…</p>
              ) : (
                <>
                  <div className="flex items-center gap-3 text-stone-500">
                    <ImageIcon className="w-5 h-5" />
                    <span className="text-stone-300">·</span>
                    <VideoIcon className="w-5 h-5" />
                  </div>
                  <p className="text-sm text-stone-700 text-center">
                    Arrastra o haz clic para subir <br />
                    <span className="text-xs text-stone-500">Imagen (JPG/PNG/WebP, máx 12 MB) o Video (MP4/WebM/MOV, máx 120 MB)</span>
                  </p>
                </>
              )}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime"
                onChange={(e) => handleUpload(e.target.files?.[0])}
                disabled={uploading}
                data-testid="portal-cms-hero-input"
                className="sr-only"
              />
            </label>
          )}
        </div>

        {/* Text fields */}
        <div className="space-y-6">
          <div>
            <label className="text-[10px] tracking-[0.32em] uppercase text-stone-500 mb-2 block">Eyebrow (kicker superior)</label>
            <Input
              value={form.eyebrow}
              onChange={(e) => setForm({ ...form, eyebrow: e.target.value })}
              placeholder="Ej. CÓMO LLEGAR"
              data-testid="portal-cms-field-eyebrow"
              className="rounded-none border-stone-300 uppercase tracking-widest"
            />
          </div>

          <div>
            <label className="text-[10px] tracking-[0.32em] uppercase text-stone-500 mb-2 block">Título principal</label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ej. La llegada es parte del viaje."
              data-testid="portal-cms-field-title"
              className="rounded-none border-stone-300 font-serif-display text-lg"
            />
          </div>

          <div>
            <label className="text-[10px] tracking-[0.32em] uppercase text-stone-500 mb-2 block">Introducción</label>
            <Textarea
              value={form.intro}
              onChange={(e) => setForm({ ...form, intro: e.target.value })}
              rows={4}
              placeholder="Párrafo introductorio de la sección…"
              data-testid="portal-cms-field-intro"
              className="rounded-none border-stone-300"
            />
          </div>

          {(selectedMeta?.critical || form.criticalNote) && (
            <div>
              <label className="flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[#B84A2C] mb-2 font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-[#B84A2C]" />
                Nota crítica (banner rojo &ldquo;Muy importante&rdquo;)
              </label>
              <Textarea
                value={form.criticalNote}
                onChange={(e) => setForm({ ...form, criticalNote: e.target.value })}
                rows={3}
                placeholder="Este texto se muestra en un banner rojo destacado."
                data-testid="portal-cms-field-critical"
                className="rounded-none border-[#B84A2C]/30 bg-[#FBEEEA]/40"
              />
            </div>
          )}
        </div>

        <div className="mt-10 pt-6 border-t border-stone-200 text-xs text-stone-500">
          <p>
            Las listas de ítems estructurados (reglas, amenidades, checklist) por ahora se editan desde el
            código — próximo paso del CMS. Hero + textos ya funcionan.
          </p>
        </div>
      </section>
    </div>
  );
}
